import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Reality = { icon: SvgIconComponent; title: string; body: string };

const realities: Reality[] = [
  {
    icon: SavingsOutlinedIcon,
    title: 'Budgets are grant-restricted and publicly scrutinized',
    body: 'Nonprofit IT spending is often earmarked by a specific grant or donor restriction, which makes a stack of separate point tools — each with its own line item — a much harder budget conversation than one consolidated bill.',
  },
  {
    icon: GroupsOutlinedIcon,
    title: 'Governance runs through a volunteer board, not an IT department',
    body: 'Technology decisions frequently go through a volunteer board meeting on a quarterly cadence, not a dedicated IT budget owner who can approve a change on the spot.',
  },
  {
    icon: Groups2OutlinedIcon,
    title: 'Staff turnover is high, and much of the team is part-time or unpaid',
    body: 'Institutional knowledge about "how our systems actually work" walks out the door with volunteers and short-tenure staff more often than at a typical small business — which makes documentation and clean account deprovisioning matter more, not less.',
  },
  {
    icon: DescriptionOutlinedIcon,
    title: 'Funders and auditors ask for proof, not promises',
    body: 'Grant compliance and annual audits expect documented controls and a reportable security posture — a similar shape of ask to a much larger organization\'s audit, without the staff a larger organization has to produce it.',
  },
];

export function SolutionsNonprofits(): JSX.Element {
  useSeo({
    title: 'For MSPs serving nonprofits',
    description:
      'One consolidated bill instead of a stack of point tools, and board-ready QBR reporting for a volunteer board that meets quarterly — built by an MSP that dogfoods Nexus on its own practice, not a nonprofit edition bolted onto a generic platform.',
    path: '/solutions/nonprofits',
  });

  return (
    <div>
      <PageHero
        eyebrow="For MSPs serving nonprofits"
        title="A grant-funded budget and a volunteer board need a different pitch than a typical SMB."
        subtitle="Nonprofit clients run on restricted budgets and board-level governance most commercial platforms weren't built around. Nexus consolidates the bill and reports in a format a quarterly board meeting can actually use."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                What&apos;s actually different about a nonprofit client
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                A nonprofit isn&apos;t a smaller, cheaper version of a commercial client.
              </h2>
              <p className="mt-4 text-white/70">
                It&apos;s a different governance and budget structure entirely — and a platform priced
                and reported around a typical SMB tends to show the mismatch quickly.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {realities.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              What that means inside Nexus
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[32px]">
              Not a nonprofit edition — the same platform, priced and reported the way a grant-funded budget needs.
            </h2>
            <ul className="mt-6 flex flex-col gap-3">
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/solutions/it-consolidation" className="text-nexus-purple-light no-underline hover:underline">
                    Consolidation
                  </Link>{' '}
                  turns a stack of separately-billed point tools into one line item — a much easier
                  thing to defend against a grant-restricted budget than five separate vendor
                  invoices.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  The{' '}
                  <Link to="/glossary/tco" className="text-nexus-purple-light no-underline hover:underline">
                    total cost of ownership
                  </Link>{' '}
                  argument for one platform over an integrated stack of point tools matters even more
                  when there&apos;s no slack in the budget to absorb integration and maintenance overhead.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/platform/compliance-qbr" className="text-nexus-purple-light no-underline hover:underline">
                    Board-ready QBR reporting
                  </Link>{' '}
                  reads the same live ticket, security, and posture data every other module has, so a
                  quarterly board meeting doesn&apos;t require rebuilding a summary from scratch by
                  hand.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  The{' '}
                  <Link to="/platform/client-staff-portal" className="text-nexus-purple-light no-underline hover:underline">
                    client &amp; staff portal
                  </Link>{' '}
                  gives volunteers and part-time staff a simple way to submit and track a ticket,
                  without needing the institutional knowledge a departed staffer took with them.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/for-small-msps" className="text-nexus-purple-light no-underline hover:underline">
                    Built for small MSPs
                  </Link>{' '}
                  matters here too — the MSPs serving nonprofits well are usually themselves running
                  lean, which is the same operating reality Nexus is built around.
                </span>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px] text-center">
          <Reveal>
            <Chip
              label="Honest about scope"
              size="small"
              className="mb-4 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              We&apos;re not pitching a &quot;nonprofit edition.&quot;
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              Nexus is one platform, not a vertical-specific fork — nothing here is nonprofit-only
              functionality. What&apos;s genuinely relevant to a nonprofit client is the same
              consolidation and reporting story that applies to any budget-constrained,
              board-governed organization, and we built it against our own lean-operating MSP
              practice before ever describing it as ready for yours.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Run your nonprofit clients on a platform built for a lean operation.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort — we&apos;ll talk through what a grant-funded,
                board-governed client actually needs, not a generic pitch.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
